import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Box, Button, IconButton, Typography } from "@mui/material";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import GenerateFileIcon from "./Components/GenerateFileIcon";
import moment from "moment";
import ImageIcon from "@mui/icons-material/Image";
import { useMemo, useState } from "react";

const SeeMoreText = ({ text }) => {
  const [currentText, setCurrentText] = useState("");
  const [collapsed, setCollapsed] = useState(false);

  useMemo(() => {
    if (text?.length > 100) {
      setCollapsed(true);
      return setCurrentText(String(text).substring(0, 100) + "...");
    }
    setCurrentText(text);
  }, [text]);

  const isAr = useMemo(() => {
    const testText = text?.slice(0, 3);
    const arabicRegex = /[\u0600-\u06FF]/;
    return arabicRegex.test(testText);
  }, [text]);

  const seeMore = () => {
    setCurrentText(text);
    setCollapsed(false);
  };

  const seeLess = () => {
    setCurrentText(String(text).substring(0, 100) + "...");
    setCollapsed(true);
  };

  return (
    <Typography component="pre" dir={isAr && "rtl"}>
      {currentText}
      {collapsed && (
        <Typography
          onClick={seeMore}
          variant="body2"
          color="primary"
          component="span"
          sx={{ cursor: "pointer" }}
        >
          See More
        </Typography>
      )}

      {!collapsed && text?.length > 100 && (
        <Typography
          onClick={seeLess}
          variant="body2"
          color="primary"
          component="span"
          sx={{ cursor: "pointer" }}
        >
          See Less
        </Typography>
      )}
    </Typography>
  );
};

export default function DataTable({
  data,
  perPage,
  handleChangePage,
  handleChangePerPage,
  page,
  count,
  onClick,
  renderCustomCells,
  extraHeaders = [],
  removePagination,
}) {
  const filteredActiveData = data?.map((item) => {
    const { createdAt, updatedAt, deletedAt, attachment, userId, ...rest } =
      item;
    return { ...rest, ...(createdAt ? { Date: createdAt } : {}) };
  });

  const headers = Object.keys(filteredActiveData?.[0] ?? []);

  const handleDownload = (fileUrl) => {
    fetch(fileUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileUrl.substring(fileUrl.lastIndexOf("/") + 1);
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "success.main";
      case "pending":
        return "warning.main";
      case "cancelled":
        return "error.main";
      default:
        return "text.primary";
    }
  };

  const getAccountTypeColor = (accountType) => {
    switch (accountType?.toLowerCase()) {
      case "supplier":
        return "rgb(0, 170, 175)";
      case "user":
        return "rgb(46, 150, 255)";
      default:
        return "text.primary";
    }
  };
  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {[...headers, ...extraHeaders]?.map((header, index) => (
                <TableCell key={index} sx={{ bgcolor: "#FAFAFA" }}>
                  {header.toUpperCase()}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredActiveData?.map((row, rowIndex) => (
              <TableRow
                sx={{ cursor: onClick && "pointer" }}
                hover
                key={rowIndex}
                onClick={() => onClick && onClick(row)}
              >
                {headers.map((key, cellIndex) => (
                  <TableCell key={cellIndex}>
                    {(["description", "comment"].includes(
                      key.toLowerCase()
                    ) && <SeeMoreText text={row[key]} />) ||
                      (key === "image" &&
                        (row[key] ? (
                          <IconButton onClick={() => window.open(row?.image)}>
                            <ImageIcon />
                          </IconButton>
                        ) : (
                          "N/A"
                        ))) ||
                      (["file", "invoice"].includes(key.toLowerCase()) ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "0.5em",
                          }}
                        >
                          {<GenerateFileIcon fileType={row[key]} />}
                          {row[key] !== null && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<FileDownloadIcon />}
                              onClick={() => handleDownload(row[key])}
                            >
                              Download
                            </Button>
                          )}
                        </Box>
                      ) : key.toLowerCase() === "status" ? (
                        <Typography
                          component="pre"
                          sx={{ color: getStatusColor(row[key]) }}
                        >
                          {row[key] || "N/A"}
                        </Typography>
                      ) : key.toLowerCase() === "accounttype" ? (
                        <Typography
                          component="pre"
                          sx={{ color: getAccountTypeColor(row[key]) }}
                        >
                          {row[key] || "N/A"}
                        </Typography>
                      ) : ["Date"].includes(key) ? (
                        row[key] ? (
                          moment(row[key]).format("DD/MM/YYYY hh:mm A")
                        ) : (
                          "N/A"
                        )
                      ) : row[key] !== null ? (
                        <Typography component="pre">{row[key]}</Typography>
                      ) : (
                        "N/A"
                      ))}
                  </TableCell>
                ))}
                {renderCustomCells ? renderCustomCells(row) : ""}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!removePagination && (
        <TablePagination
          perPageOptions={[10, 25]}
          count={count}
          rowsPerPage={perPage}
          page={page - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangePerPage}
        />
      )}
    </Paper>
  );
}
